import { Controller } from "@hotwired/stimulus"
import { formatMoney, centsToDecimal } from "../support/format_money"

/**
 * Base dives controller — shared diver/date/product state machine.
 *
 * Manages:
 *   - Multi-diver state (add/remove/rename)
 *   - Per-diver date assignment
 *   - Per-date product/addon selection via modals
 *   - Order summary rendering
 *   - Hidden input sync for form submission
 *   - Public API: getSelectionSummary(), getGrandTotal(), getDiverCount()
 *
 * POS-specific features NOT included here (added by pos_dives_controller):
 *   - Diver search (searchDiverForSlot, _fetchDiverResults, _assignDiverToSlot, unlinkDiver)
 *   - Inline price editing (updatePrice)
 *   - Copy to all divers (copyToAllDivers)
 *
 * Values:
 *   products (Array)  — dive product catalog [{value, title, description, category, dive_type, price_cents}]
 *   addons (Array)     — equipment/additional offerings [{id, name, category, price_cents}]
 */
export default class extends Controller {
  static targets = ["diverSections", "orderSummary", "grandTotal", "hiddenInputs", "specialRequestsMobile", "specialRequestsDesktop"]

  static values = {
    products: Array,
    addons: Array,
    initialBooking: { type: Object, default: {} }
  }

  static CATEGORY_ORDER = ["fun_dive", "experience", "package", "course"]
  static CATEGORY_LABELS = {
    fun_dive: "Fun Dives",
    experience: "Experience Dives",
    package: "Packages",
    course: "Courses"
  }

  static PACKAGE_ICON = '<svg class="w-3.5 h-3.5 text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>'
  static ADDON_ICON = '<svg class="w-3.5 h-3.5 text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/></svg>'

  /**
   * The Stimulus identifier used in data-action attributes.
   * Override in subclass to match its registered name.
   */
  get actionIdentifier() {
    return this.identifier
  }

  syncSpecialRequests(event) {
    const value = event.target.value
    if (this.hasSpecialRequestsMobileTarget && this.specialRequestsMobileTarget !== event.target) {
      this.specialRequestsMobileTarget.value = value
    }
    if (this.hasSpecialRequestsDesktopTarget && this.specialRequestsDesktopTarget !== event.target) {
      this.specialRequestsDesktopTarget.value = value
    }
  }

  connect() {
    this.nextDiverId = 2
    this.allSelectedDates = new Set()

    const editData = this.initialBookingValue
    if (editData && editData.divers && editData.divers.length > 0) {
      this.divers = editData.divers.map(d => ({
        id: d.id,
        position: d.position,
        label: d.label,
        diverId: d.diverId,
        diverName: d.diverName,
        dates: d.dates || {}
      }))
      this.nextDiverId = editData.nextDiverId || 2

      // Populate allSelectedDates from loaded diver data
      this.divers.forEach(d => {
        Object.keys(d.dates).forEach(date => {
          if (date !== "__shared__") this.allSelectedDates.add(date)
        })
      })
    } else {
      this.divers = this.buildInitialDivers()
    }

    this.render()
  }

  /**
   * Build the initial divers array. Override in subclass to add
   * shared fees or other default divers.
   */
  buildInitialDivers() {
    return [
      {
        id: "diver_1",
        position: 1,
        label: "Diver 1 Full Name",
        diverId: null,
        diverName: null,
        dates: {}
      }
    ]
  }

  // ===== Called by wizard controller after Guest step =====

  setPrimaryDiver(diverId, name) {
    const primary = this.divers.find(d => d.position === 1)
    if (primary) {
      primary.diverId = diverId
      primary.diverName = name
      primary.label = name || "Diver 1 Full Name"
      this.renderDiverSections()
      this.renderOrderSummary()
      this.syncHiddenInputs()
    }
  }

  // ===== Date Events (from date-picker controller) =====

  dateToggled(event) {
    this._mutateForDateToggle(event)
    this.render()
  }

  // Mutates state (allSelectedDates + per-diver date entries) without
  // rendering. Subclasses can call this directly when they need to do
  // additional bookkeeping before a single final render.
  _mutateForDateToggle(event) {
    const { date, selected } = event.detail

    if (selected) {
      this.allSelectedDates.add(date)
      // Auto-assign to all divers (except shared fees position 0)
      this.divers.forEach(d => {
        if (d.position !== 0 && !d.dates[date]) {
          d.dates[date] = { products: [], addons: [], disabled: false }
        }
      })
    } else {
      this.allSelectedDates.delete(date)
      // Remove from all divers (except shared fees position 0)
      this.divers.forEach(d => {
        if (d.position !== 0) delete d.dates[date]
      })
    }
  }

  // ===== Diver Management =====

  addDiver(event) {
    event.preventDefault()
    const id = `diver_${this.nextDiverId}`
    const position = this.nextDiverId
    this.nextDiverId++

    // Insert before "shared" if it exists, otherwise append
    const sharedIdx = this.divers.findIndex(d => d.position === 0)
    // Auto-populate all calendar-selected dates for the new diver
    const diverDates = {}
    this.allSelectedDates.forEach(date => {
      diverDates[date] = { products: [], addons: [], disabled: false }
    })

    const newDiver = {
      id,
      position,
      label: `Diver ${position} Full Name`,
      diverId: null,
      diverName: null,
      dates: diverDates
    }

    if (sharedIdx !== -1) {
      this.divers.splice(sharedIdx, 0, newDiver)
    } else {
      this.divers.push(newDiver)
    }

    this.render()
  }

  removeDiver(event) {
    event.preventDefault()
    event.stopPropagation()
    const diverId = event.currentTarget.dataset.diverKey
    const idx = this.divers.findIndex(d => d.id === diverId)
    if (idx === -1 || this.divers[idx].position === 1 || this.divers[idx].position === 0) return

    this.divers.splice(idx, 1)
    this.render()
  }

  renameDiver(event) {
    const diverId = event.currentTarget.dataset.diverKey
    const diver = this.divers.find(d => d.id === diverId)
    if (!diver) return
    diver.label = event.currentTarget.value || diver.label
    this.renderOrderSummary()
    this.syncHiddenInputs()
  }

  // ===== Per-Diver Date Management =====

  // Disable a date for a diver (dims it, hides its products/addons)
  disableDateForDiver(event) {
    event.preventDefault()
    event.stopPropagation()
    const diverKey = event.currentTarget.dataset.diverKey
    const date = event.currentTarget.dataset.date

    const diver = this.divers.find(d => d.id === diverKey)
    if (diver && diver.dates[date]) {
      diver.dates[date].disabled = true
      this.render()
    }
  }

  // Re-enable a disabled date for a diver
  enableDateForDiver(event) {
    event.preventDefault()
    event.stopPropagation()
    const diverKey = event.currentTarget.dataset.diverKey
    const date = event.currentTarget.dataset.date

    const diver = this.divers.find(d => d.id === diverKey)
    if (diver && diver.dates[date]) {
      diver.dates[date].disabled = false
      this.render()
    }
  }

  // ===== Product / Add-on via Modal =====

  openProductModal(event) {
    event.preventDefault()
    event.stopPropagation()
    const diverKey = event.currentTarget.dataset.diverKey
    const date = event.currentTarget.dataset.date
    this._showModal("product", diverKey, date)
  }

  openAddonModal(event) {
    event.preventDefault()
    event.stopPropagation()
    const diverKey = event.currentTarget.dataset.diverKey
    const date = event.currentTarget.dataset.date
    this._showModal("addon", diverKey, date)
  }

  selectProductFromModal(event) {
    event.preventDefault()
    const diverKey = event.currentTarget.dataset.diverKey
    const date = event.currentTarget.dataset.date
    const value = event.currentTarget.dataset.value
    const priceCents = parseInt(event.currentTarget.dataset.priceCents) || 0

    const diver = this.divers.find(d => d.id === diverKey)
    if (!diver || !diver.dates[date]) return

    diver.dates[date].products.push({ value, qty: 1, price_cents: priceCents })
    this._closeModal()
    this.render()
  }

  selectAddonFromModal(event) {
    event.preventDefault()
    const diverKey = event.currentTarget.dataset.diverKey
    const date = event.currentTarget.dataset.date
    const id = event.currentTarget.dataset.addonId
    const priceCents = parseInt(event.currentTarget.dataset.priceCents) || 0

    const diver = this.divers.find(d => d.id === diverKey)
    if (!diver || !diver.dates[date]) return

    diver.dates[date].addons.push({ id, qty: 1, price_cents: priceCents })
    this._closeModal()
    this.render()
  }

  closeModal(event) {
    event.preventDefault()
    this._closeModal()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ===== Inline Changes =====

  changeProduct(event) {
    const diverKey = event.currentTarget.dataset.diverKey
    const date = event.currentTarget.dataset.date
    const index = parseInt(event.currentTarget.dataset.index)
    const newValue = event.currentTarget.value

    const diver = this.divers.find(d => d.id === diverKey)
    if (!diver || !diver.dates[date]) return

    const prod = this._productByValue(newValue)
    if (prod && diver.dates[date].products[index]) {
      diver.dates[date].products[index].value = newValue
      diver.dates[date].products[index].price_cents = prod.price_cents
    }
    this.render()
  }

  changeAddon(event) {
    const diverKey = event.currentTarget.dataset.diverKey
    const date = event.currentTarget.dataset.date
    const index = parseInt(event.currentTarget.dataset.index)
    const newId = event.currentTarget.value

    const diver = this.divers.find(d => d.id === diverKey)
    if (!diver || !diver.dates[date]) return

    const addon = this._addonById(newId)
    if (addon && diver.dates[date].addons[index]) {
      diver.dates[date].addons[index].id = newId
      diver.dates[date].addons[index].price_cents = addon.price_cents
    }
    this.render()
  }

  // ===== Remove Row =====

  removeProduct(event) {
    event.preventDefault()
    event.stopPropagation()
    const diverKey = event.currentTarget.dataset.diverKey
    const date = event.currentTarget.dataset.date
    const index = parseInt(event.currentTarget.dataset.index)

    const diver = this.divers.find(d => d.id === diverKey)
    if (diver && diver.dates[date]) {
      diver.dates[date].products.splice(index, 1)
      this.render()
    }
  }

  removeAddon(event) {
    event.preventDefault()
    event.stopPropagation()
    const diverKey = event.currentTarget.dataset.diverKey
    const date = event.currentTarget.dataset.date
    const index = parseInt(event.currentTarget.dataset.index)

    const diver = this.divers.find(d => d.id === diverKey)
    if (diver && diver.dates[date]) {
      diver.dates[date].addons.splice(index, 1)
      this.render()
    }
  }

  // ===== Quantity =====

  incrementQty(event) {
    event.preventDefault()
    this._adjustQty(event.currentTarget, 1)
  }

  decrementQty(event) {
    event.preventDefault()
    this._adjustQty(event.currentTarget, -1)
  }

  updateQty(event) {
    const el = event.currentTarget
    const item = this._findItem(el.dataset.diverKey, el.dataset.date, el.dataset.itemType, el.dataset.value)
    if (item) {
      item.qty = Math.max(1, parseInt(el.value) || 1)
      this.renderOrderSummary()
      this.syncHiddenInputs()
    }
  }

  // ===== Shared Fees =====

  addSharedFee(event) {
    event.preventDefault()
    event.stopPropagation()
    this._showModal("addon", "shared", "__shared__")
  }

  addSharedProduct(event) {
    event.preventDefault()
    event.stopPropagation()
    this._showModal("product", "shared", "__shared__")
  }

  // ===== Public API for wizard controller =====

  getSelectionSummary() {
    const result = []

    this.divers.forEach(diver => {
      if (diver.position === 0) {
        // Shared fees
        const sharedData = diver.dates["__shared__"]
        if (!sharedData) return
        const items = this._collectItems(sharedData)
        if (items.length === 0) return
        result.push({ label: "Shared Fees", diverKey: diver.id, position: 0, items })
        return
      }

      const dates = Object.keys(diver.dates).sort()
      const items = []

      // Check if this diver is in package mode (method exists on public subclass)
      const isPackage = this._isPackageDiver && this._isPackageDiver(diver.id)
      const pkgProduct = isPackage && this._getPackageProduct ? this._getPackageProduct(diver.id) : null

      if (isPackage && pkgProduct) {
        // Package-mode diver: include the package as an item.
        // `value` is required so cross-controller listeners (e.g. public-
        // accommodation#_deriveMode) can match this item against the
        // packageConfigs catalog by its stable "category:slug" id.
        items.push({
          label: pkgProduct.title,
          value: pkgProduct.value,
          qty: 1,
          price_cents: pkgProduct.price_cents,
          category: pkgProduct.category
        })
        // Also collect any add-ons from each date
        dates.forEach(date => {
          const dateData = diver.dates[date]
          const dateLabel = this._formatDate(date)
          dateData.addons.forEach(a => {
            const addon = this._addonById(a.id)
            if (addon) items.push({ label: addon.name, qty: a.qty, price_cents: a.price_cents, isAddon: true, dateLabel })
          })
        })
      } else {
        // Standard diver: collect items from each date
        dates.forEach(date => {
          const dateData = diver.dates[date]
          const dateLabel = this._formatDate(date)
          this._collectItems(dateData).forEach(item => {
            items.push({ ...item, dateLabel })
          })
        })
      }

      if (items.length === 0 && dates.length === 0) return

      const dateStr = dates.map(d => this._formatDateShort(d)).join(", ")
      result.push({
        label: diver.label,
        diverKey: diver.id,
        position: diver.position,
        diverId: diver.diverId,
        dates: dateStr,
        items
      })
    })

    return result
  }

  getGrandTotal() {
    return this._grandTotal()
  }

  getDiverCount() {
    return this.divers.filter(d => d.position > 0).length
  }

  // ===== Private Helpers =====

  _collectItems(dateData) {
    const items = []
    dateData.products.forEach(p => {
      const prod = this._productByValue(p.value)
      if (prod) items.push({ label: prod.title, value: prod.value, qty: p.qty, price_cents: p.price_cents, category: prod.category })
    })
    dateData.addons.forEach(a => {
      const addon = this._addonById(a.id)
      if (addon) items.push({ label: addon.name, qty: a.qty, price_cents: a.price_cents, isAddon: true, category: addon.category })
    })
    return items
  }

  _adjustQty(button, delta) {
    const item = this._findItem(button.dataset.diverKey, button.dataset.date, button.dataset.itemType, button.dataset.value)
    if (item) {
      item.qty = Math.max(1, item.qty + delta)
      this.render()
    }
  }

  _findItem(diverKey, date, type, value) {
    const diver = this.divers.find(d => d.id === diverKey)
    if (!diver || !diver.dates[date]) return null
    if (type === "product") {
      return diver.dates[date].products.find(p => p.value === value)
    }
    return diver.dates[date].addons.find(a => a.id === value)
  }

  _productByValue(value) {
    return this.productsValue.find(p => p.value === value)
  }

  _addonById(id) {
    return this.addonsValue.find(a => String(a.id) === String(id))
  }

  _formatDate(dateStr) {
    if (dateStr === "__shared__") return ""
    const d = new Date(dateStr + "T00:00:00")
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  }

  _formatDateShort(dateStr) {
    if (dateStr === "__shared__") return ""
    const d = new Date(dateStr + "T00:00:00")
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  }

  _formatPrice(cents) {
    return formatMoney(cents)
  }

  _diverSubtotal(diver) {
    // For package-mode divers, count the package price once (from catalog)
    // plus all addons and non-package products, regardless of disabled dates
    if (this._isPackageDiver && this._isPackageDiver(diver.id)) {
      const pkgProduct = this._getPackageProduct(diver.id)
      const pkgInfo = this._getPackageInfo(diver.id)
      let total = pkgProduct ? pkgProduct.price_cents : 0

      for (const date of Object.keys(diver.dates)) {
        const data = diver.dates[date]
        if (date !== "__shared__" && data.disabled) continue
        data.products.forEach(p => {
          if (pkgInfo && p.value === pkgInfo.packageValue) return // skip package product (already counted)
          total += p.qty * p.price_cents
        })
        data.addons.forEach(a => { total += a.qty * a.price_cents })
      }
      return total
    }

    let total = 0
    for (const date of Object.keys(diver.dates)) {
      const data = diver.dates[date]
      if (date !== "__shared__" && data.disabled) continue
      data.products.forEach(p => { total += p.qty * p.price_cents })
      data.addons.forEach(a => { total += a.qty * a.price_cents })
    }
    return total
  }

  _grandTotal() {
    let total = 0
    this.divers.forEach(d => { total += this._diverSubtotal(d) })
    return total
  }

  _showToast(message) {
    const toast = document.createElement("div")
    toast.className = "fixed bottom-4 right-4 bg-gray-900 dark:bg-base-100 text-white dark:text-base-900 px-4 py-2 rounded-lg shadow-lg text-sm z-50"
    toast.textContent = message
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 3000)
  }

  // ===== Modal =====

  _showModal(type, diverKey, date) {
    this._closeModal()

    const content = type === "product"
      ? this._renderProductModalContent(diverKey, date)
      : this._renderAddonModalContent(diverKey, date)

    const title = type === "product" ? "Add Dive Package" : "Add Add-on"
    const id = this.actionIdentifier

    const modal = document.createElement("div")
    modal.id = "dives-modal"
    modal.className = "bw-modal-overlay"
    modal.innerHTML = `
      <div class="absolute inset-0 bg-black/40" data-action="click->${id}#closeModal"></div>
      <div class="bw-modal-panel relative bg-white dark:bg-base-800 rounded-2xl flex flex-col" data-action="click->${id}#stopPropagation">
        <div class="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-200 dark:border-base-700 shrink-0">
          <h3 class="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate">${title}</h3>
          <button type="button" class="shrink-0 w-10 h-10 -mr-2 rounded-md text-gray-400 dark:text-base-500 hover:text-gray-600 dark:hover:text-base-300 hover:bg-gray-100 dark:hover:bg-base-700 inline-flex items-center justify-center text-xl"
                  data-action="click->${id}#closeModal" aria-label="Close">&times;</button>
        </div>
        <div class="overflow-y-auto p-4 sm:p-5 pb-[max(1rem,env(safe-area-inset-bottom))]">${content}</div>
      </div>`

    this.element.appendChild(modal)
  }

  _closeModal() {
    const modal = this.element.querySelector("#dives-modal")
    if (modal) modal.remove()
  }

  _renderProductModalContent(diverKey, date) {
    const grouped = {}
    const id = this.actionIdentifier
    this.constructor.CATEGORY_ORDER.forEach(cat => { grouped[cat] = [] })
    this.productsValue.forEach(prod => {
      if (prod.category in grouped) grouped[prod.category].push(prod)
    })

    return this.constructor.CATEGORY_ORDER
      .filter(cat => grouped[cat].length > 0)
      .map(cat => {
        const cards = grouped[cat].map(prod => `
          <div class="flex items-center justify-between p-3 border border-gray-200 dark:border-base-700 rounded-lg cursor-pointer hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all"
               data-action="click->${id}#selectProductFromModal"
               data-diver-key="${diverKey}" data-date="${date}" data-value="${prod.value}" data-price-cents="${prod.price_cents}">
            <div class="min-w-0">
              <div class="font-semibold text-sm text-gray-900 dark:text-white">${this._escapeHtml(prod.title)}</div>
              ${prod.description ? `<div class="text-xs text-gray-500 dark:text-base-400 mt-0.5">${this._escapeHtml(prod.description)}</div>` : ""}
            </div>
            <span class="font-bold text-blue-600 dark:text-blue-400 text-sm shrink-0 ml-3">${this._formatPrice(prod.price_cents)}</span>
          </div>`).join("")

        return `
          <div class="mb-4">
            <h5 class="text-xs font-semibold text-gray-500 dark:text-base-400 uppercase tracking-wide mb-2">${this.constructor.CATEGORY_LABELS[cat]}</h5>
            <div class="bw-modal-grid">${cards}</div>
          </div>`
      }).join("")
  }

  _renderAddonModalContent(diverKey, date) {
    const equipment = this.addonsValue.filter(a => a.category === "equipment")
    const additional = this.addonsValue.filter(a => a.category !== "equipment")
    const id = this.actionIdentifier

    const renderGroup = (items, label) => {
      if (!items.length) return ""
      const cards = items.map(addon => `
        <div class="flex items-center justify-between p-3 border border-gray-200 dark:border-base-700 rounded-lg cursor-pointer hover:border-green-300 dark:hover:border-green-500 hover:bg-green-50/50 dark:hover:bg-green-900/20 transition-all"
             data-action="click->${id}#selectAddonFromModal"
             data-diver-key="${diverKey}" data-date="${date}" data-addon-id="${addon.id}" data-price-cents="${addon.price_cents}">
          <div class="text-sm font-semibold text-gray-900 dark:text-white">${this._escapeHtml(addon.name)}</div>
          <span class="font-bold text-blue-600 dark:text-blue-400 text-sm shrink-0 ml-3">${this._formatPrice(addon.price_cents)}</span>
        </div>`).join("")

      return `
        <div class="mb-4">
          <h5 class="text-xs font-semibold text-gray-500 dark:text-base-400 uppercase tracking-wide mb-2">${label}</h5>
          <div class="bw-modal-grid">${cards}</div>
        </div>`
    }

    return renderGroup(equipment, "Equipment Rentals") + renderGroup(additional, "Additional Offerings")
  }

  _productOptionsHtml(selectedValue) {
    return this.productsValue.map(prod =>
      `<option value="${prod.value}" ${prod.value === selectedValue ? "selected" : ""}>${this._escapeHtml(prod.title)} — ${this._formatPrice(prod.price_cents)}</option>`
    ).join("")
  }

  _addonOptionsHtml(selectedId) {
    return this.addonsValue.map(addon =>
      `<option value="${addon.id}" ${String(addon.id) === String(selectedId) ? "selected" : ""}>${this._escapeHtml(addon.name)} — ${this._formatPrice(addon.price_cents)}</option>`
    ).join("")
  }

  // ===== Rendering =====

  render() {
    this.renderDiverSections()
    this.renderOrderSummary()
    this.syncHiddenInputs()
    this._dispatchChanges()
  }

  _dispatchChanges() {
    // Bubbles to the form root so sibling controllers can listen via
    // data-action. Two events because consumers care about different slices:
    //   datesChanged    → coverage check (dive_dates)
    //   selectionChanged → mode derivation (which products are selected)
    this.dispatch("datesChanged", {
      detail: { diveDates: [...this.allSelectedDates].sort() },
      bubbles: true
    })
    this.dispatch("selectionChanged", {
      detail: { selectionSummary: this.getSelectionSummary() },
      bubbles: true
    })
  }

  renderDiverSections() {
    if (!this.hasDiverSectionsTarget) return

    const html = this.divers.map(diver => {
      if (diver.position === 0) return this._renderSharedFeesSection(diver)

      // Check if subclass has package mode for this diver
      if (this._isPackageDiver && this._isPackageDiver(diver.id)) {
        const pkgProduct = this._getPackageProduct(diver.id)
        const pkgInfo = this._getPackageInfo(diver.id)
        if (pkgProduct && pkgInfo) {
          return this._renderPackageDiverSection(diver, pkgProduct, pkgInfo, (did, date) => this._getPackageDayNumber(did, date))
        }
      }

      return this._renderDiverSection(diver)
    }).join("")

    this.diverSectionsTarget.innerHTML = html
  }

  /**
   * Render a single diver section. Override in subclass to add
   * features like diver search or price editing.
   */
  _renderDiverSection(diver) {
    const isPrimary = diver.position === 1
    const dates = Object.keys(diver.dates).filter(d => d !== "__shared__").sort()
    const subtotal = this._diverSubtotal(diver)
    const badge = isPrimary ? "Primary Guest" : ""
    const id = this.actionIdentifier

    // Date chips — enabled dates show X button, disabled dates are dimmed and clickable to re-enable
    const dateChips = dates.map(date => {
      const isDisabled = diver.dates[date]?.disabled
      if (isDisabled) {
        return `
          <button type="button" class="inline-flex items-center gap-1 bg-gray-100 dark:bg-base-700 text-gray-400 dark:text-base-500 text-xs px-2 py-0.5 rounded-full opacity-50 cursor-pointer"
                  data-action="click->${id}#enableDateForDiver" data-diver-key="${diver.id}" data-date="${date}">
            ${this._formatDateShort(date)}
          </button>`
      }
      return `
        <span class="inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs px-2 py-0.5 rounded-full">
          ${this._formatDateShort(date)}
          <button type="button" class="hover:text-red-500 dark:hover:text-red-300 text-xs leading-none"
                  data-action="click->${id}#disableDateForDiver" data-diver-key="${diver.id}" data-date="${date}">&times;</button>
        </span>`
    }).join("")

    // Per-date product/addon sections — only for enabled dates
    const enabledDates = dates.filter(date => !diver.dates[date]?.disabled)
    const dateSections = enabledDates.map(date => this._renderDateWithinDiver(diver, date)).join("")

    // Diver name display — subclass can override _renderDiverNameField for search support
    const nameField = this._renderDiverNameField(diver)

    return `
      <div class="border border-gray-200 dark:border-base-700 rounded-xl mb-3 bg-white dark:bg-base-800 shadow-sm" data-diver-section="${diver.id}">
        <div class="flex items-center justify-between p-4 border-b border-gray-100 dark:border-base-700">
          <div class="flex items-center gap-3 min-w-0 flex-wrap">
            <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
              <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </div>
            ${nameField}
            ${badge ? `<span class="text-xs text-blue-500 dark:text-blue-300 font-medium">${badge}</span>` : ""}
            ${subtotal > 0 ? `<span class="text-sm font-bold text-blue-600 dark:text-blue-400">${this._formatPrice(subtotal)}</span>` : ""}
          </div>
          <div class="flex items-center gap-2 shrink-0">
            ${!isPrimary ? `
              <button type="button" class="w-6 h-6 rounded-full text-gray-400 dark:text-base-500 hover:text-red-500 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-lg leading-none"
                      data-action="click->${id}#removeDiver" data-diver-key="${diver.id}">&times;</button>
            ` : ""}
          </div>
        </div>

        ${dateChips ? `<div class="flex flex-wrap items-center gap-1.5 px-4 py-2 border-b border-gray-200 dark:border-base-700"><span class="text-xs font-semibold text-gray-400 dark:text-base-500 shrink-0 mr-1">Dive dates</span>${dateChips}</div>` : ""}

        <div class="p-4 space-y-4">
          ${dateSections || `<p class="text-sm text-gray-400 dark:text-base-500 italic text-center py-2">No dates assigned. Select dates on the calendar.</p>`}
        </div>
      </div>`
  }

  /**
   * Render a diver section in package mode.
   * Shows read-only package items grouped by day, with a remove-package button.
   *
   * @param {Object} diver - The diver object
   * @param {Object} pkgProduct - The package product from productsValue (with package_items, duration_days)
   * @param {Object} pkgInfo - The packageMode.divers entry (packageValue, durationDays, assignedDates)
   * @param {Function} getDayNumber - Function(diverId, date) => dayNumber or null
   */
  _renderPackageDiverSection(diver, pkgProduct, pkgInfo, getDayNumber) {
    const isPrimary = diver.position === 1
    const subtotal = this._diverSubtotal(diver)
    const badge = isPrimary ? "Primary Guest" : ""
    const id = this.actionIdentifier
    const dates = Object.keys(diver.dates).filter(d => d !== "__shared__").sort()
    const assignedDates = pkgInfo.assignedDates || []
    const durationDays = pkgInfo.durationDays || 0
    const packageItems = pkgProduct.package_items || []

    // Diver name field
    const nameField = this._renderDiverNameField(diver)

    // Date chips
    const dateChips = dates.map(date => {
      const isDisabled = diver.dates[date]?.disabled
      if (isDisabled) {
        return `
          <button type="button" class="inline-flex items-center gap-1 bg-gray-100 dark:bg-base-700 text-gray-400 dark:text-base-500 text-xs px-2 py-0.5 rounded-full opacity-50 cursor-pointer"
                  data-action="click->${id}#enableDateForDiver" data-diver-key="${diver.id}" data-date="${date}">
            ${this._formatDateShort(date)}
          </button>`
      }
      return `
        <span class="inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs px-2 py-0.5 rounded-full">
          ${this._formatDateShort(date)}
          <button type="button" class="hover:text-red-500 dark:hover:text-red-300 text-xs leading-none"
                  data-action="click->${id}#disableDateForDiver" data-diver-key="${diver.id}" data-date="${date}">&times;</button>
        </span>`
    }).join("")

    // Warning banner (fewer dates than duration)
    const selectedCount = dates.filter(d => !diver.dates[d]?.disabled).length
    const warningHtml = selectedCount < durationDays
      ? `<div class="mx-4 mb-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-700 dark:text-amber-300">
           <strong>${this._escapeHtml(pkgProduct.title)}</strong> covers ${durationDays} dive day${durationDays === 1 ? "" : "s"} — you've selected ${selectedCount}
         </div>`
      : ""

    // Package header bar with remove button
    const packageHeader = `
      <div class="d-flex align-items-center justify-content-between mx-3 mb-3 px-3 py-2 rounded" style="background: rgba(59,130,246,0.1); border: 1px solid rgba(96,165,250,0.3);">
        <div class="d-flex align-items-center gap-2">
          ${this.constructor.PACKAGE_ICON}
          <span class="small fw-semibold" style="color: #93c5fd;">${this._escapeHtml(pkgProduct.title)}</span>
          <span class="small fw-bold" style="color: #60a5fa;">${this._formatPrice(pkgProduct.price_cents)}</span>
        </div>
        <button type="button"
                class="btn btn-sm btn-outline-danger"
                style="font-size: 0.75rem; padding: 0.2rem 0.6rem; --bs-btn-color: #fca5a5; --bs-btn-border-color: rgba(239,68,68,0.4); --bs-btn-hover-color: #fecaca; --bs-btn-hover-border-color: rgba(239,68,68,0.6); --bs-btn-hover-bg: rgba(239,68,68,0.1);"
                data-action="click->${id}#removePackage" data-diver-key="${diver.id}">
          Remove Package
        </button>
      </div>`

    // Render day cards for assigned dates
    const dayCards = []
    for (let dayNum = 1; dayNum <= durationDays; dayNum++) {
      const date = assignedDates[dayNum - 1] || null
      const dayItems = packageItems.filter(item => item.day_number === dayNum)
      const dateLabel = date ? this._formatDate(date) : "No date selected"
      const isUnassigned = !date

      // Read-only package item badges
      const itemBadges = dayItems.map(item =>
        `<span class="inline-flex items-center gap-1 text-xs ${isUnassigned ? "text-gray-300 dark:text-base-600" : "text-gray-600 dark:text-base-300"} bg-white dark:bg-base-800 border border-gray-200 dark:border-base-700 rounded-full px-2 py-0.5">
          ${item.quantity}x ${this._escapeHtml(item.name)}
        </span>`
      ).join("")

      // Add-on rows for this date (if assigned)
      const addonRows = date && diver.dates[date]
        ? diver.dates[date].addons.map((a, index) => this._renderAddonRow(diver.id, date, a, index)).join("")
        : ""

      dayCards.push(`
        <div class="border border-gray-100 dark:border-base-700 rounded-lg p-3 ${isUnassigned ? "bg-gray-50/30 dark:bg-base-900/30 opacity-60" : "bg-gray-50/50 dark:bg-base-900/50"}">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-bold ${isUnassigned ? "text-gray-300 dark:text-base-600" : "text-gray-500 dark:text-base-400"} uppercase tracking-wide">
              ${dateLabel} <span class="text-gray-300 dark:text-base-600 font-normal">(Day ${dayNum})</span>
            </span>
            ${date ? `
              <button type="button" class="text-xs font-medium text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 flex items-center gap-1"
                      data-action="click->${id}#openAddonModal" data-diver-key="${diver.id}" data-date="${date}">
                <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M12 4v16m8-8H4"/></svg>
                Add-on
              </button>
            ` : ""}
          </div>
          <div class="flex flex-wrap gap-1.5 ${addonRows ? "mb-2" : ""}">
            ${itemBadges || `<span class="text-xs text-gray-300 dark:text-base-600 italic">No items for this day</span>`}
          </div>
          ${addonRows ? `<div class="space-y-2 pt-2 border-t border-gray-100 dark:border-base-700">${addonRows}</div>` : ""}
        </div>`)
    }

    // Package-wide items (day_number is null)
    const allDayItems = packageItems.filter(item => item.day_number === null || item.day_number === undefined)
    const allDayCard = allDayItems.length > 0 ? `
      <div class="border border-gray-100 dark:border-base-700 rounded-lg p-3 bg-gray-50/50 dark:bg-base-900/50">
        <div class="mb-2">
          <span class="text-xs font-bold text-gray-500 dark:text-base-400 uppercase tracking-wide">All Days</span>
        </div>
        <div class="flex flex-wrap gap-1.5">
          ${allDayItems.map(item =>
            `<span class="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-base-300 bg-white dark:bg-base-800 border border-gray-200 dark:border-base-700 rounded-full px-2 py-0.5">
              ${item.quantity}x ${this._escapeHtml(item.name)}
            </span>`
          ).join("")}
        </div>
      </div>` : ""

    // Extra dates beyond package duration (standard day cards with full controls)
    const extraDates = dates.filter(d => !diver.dates[d]?.disabled && !assignedDates.includes(d))
    const extraDateCards = extraDates.map(date => this._renderDateWithinDiver(diver, date)).join("")

    return `
      <div class="border border-gray-200 dark:border-base-700 rounded-xl mb-3 bg-white dark:bg-base-800 shadow-sm" data-diver-section="${diver.id}">
        <div class="flex items-center justify-between p-4 border-b border-gray-100 dark:border-base-700">
          <div class="flex items-center gap-3 min-w-0 flex-wrap">
            <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
              <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </div>
            ${nameField}
            ${badge ? `<span class="text-xs text-blue-500 dark:text-blue-300 font-medium">${badge}</span>` : ""}
            ${subtotal > 0 ? `<span class="text-sm font-bold text-blue-600 dark:text-blue-400">${this._formatPrice(subtotal)}</span>` : ""}
          </div>
          <div class="flex items-center gap-2 shrink-0">
            ${!isPrimary ? `
              <button type="button" class="w-6 h-6 rounded-full text-gray-400 dark:text-base-500 hover:text-red-500 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-lg leading-none"
                      data-action="click->${id}#removeDiver" data-diver-key="${diver.id}">&times;</button>
            ` : ""}
          </div>
        </div>

        ${dateChips ? `<div class="flex flex-wrap items-center gap-1.5 px-4 py-2 border-b border-gray-200 dark:border-base-700"><span class="text-xs font-semibold text-gray-400 dark:text-base-500 shrink-0 mr-1">Dive dates</span>${dateChips}</div>` : ""}

        ${packageHeader}
        ${warningHtml}

        <div class="px-4 pb-4 space-y-3">
          ${dayCards.join("")}
          ${allDayCard}
          ${extraDateCards ? `<div class="pt-2 border-t border-gray-200 dark:border-base-700 space-y-3">${extraDateCards}</div>` : ""}
        </div>
      </div>`
  }

  /**
   * Render the diver name field. Override in subclass to add
   * diver search button or linked-diver display.
   */
  _renderDiverNameField(diver) {
    const id = this.actionIdentifier
    return `<input type="text" value="${this._escapeAttr(diver.label)}"
               class="text-sm font-semibold text-gray-900 dark:text-white border-0 border-b border-transparent hover:border-gray-300 dark:hover:border-base-600 focus:border-blue-400 dark:focus:border-blue-500 focus:ring-0 px-0 py-0.5 bg-transparent w-32"
               data-action="change->${id}#renameDiver" data-diver-key="${diver.id}">`
  }

  _renderDateWithinDiver(diver, date) {
    const sel = diver.dates[date]
    const label = this._formatDate(date)
    const id = this.actionIdentifier

    const productRows = sel.products.map((p, index) => this._renderProductRow(diver.id, date, p, index)).join("")
    const addonRows = sel.addons.map((a, index) => this._renderAddonRow(diver.id, date, a, index)).join("")

    return `
      <div class="border border-gray-100 dark:border-base-700 rounded-lg p-3 bg-gray-50/50 dark:bg-base-900/50">
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs font-bold text-gray-500 dark:text-base-400 uppercase tracking-wide">${label}</span>
          <div class="flex items-center gap-2">
            <button type="button" class="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 flex items-center gap-1"
                    data-action="click->${id}#openProductModal" data-diver-key="${diver.id}" data-date="${date}">
              <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M12 4v16m8-8H4"/></svg>
              Package
            </button>
            <button type="button" class="text-xs font-medium text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 flex items-center gap-1"
                    data-action="click->${id}#openAddonModal" data-diver-key="${diver.id}" data-date="${date}">
              <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M12 4v16m8-8H4"/></svg>
              Add-on
            </button>
          </div>
        </div>

        ${productRows ? `<div class="space-y-2 mb-2">${productRows}</div>` : ""}
        ${addonRows ? `<div class="space-y-2">${addonRows}</div>` : ""}
        ${!productRows && !addonRows ? `<p class="text-xs text-gray-400 dark:text-base-500 italic">No items — add a package or add-on above</p>` : ""}
      </div>`
  }

  /**
   * Render a product row. Override in subclass to add price editing
   * or copy-to-all button.
   */
  _renderProductRow(diverKey, date, p, index) {
    const id = this.actionIdentifier
    return `
      <div class="flex items-center gap-2 flex-wrap sm:flex-nowrap">
        <select class="flex-1 min-w-0 text-sm border border-gray-300 dark:border-base-600 rounded-md py-1.5 px-2 bg-white dark:bg-base-800 dark:text-white focus:border-blue-400 dark:focus:border-blue-500 focus:ring-1 focus:ring-blue-400 dark:focus:ring-blue-500"
                data-action="change->${id}#changeProduct" data-diver-key="${diverKey}" data-date="${date}" data-index="${index}">
          ${this._productOptionsHtml(p.value)}
        </select>
        <div class="flex items-center gap-1 shrink-0">
          <button type="button" class="w-7 h-7 rounded border border-gray-300 dark:border-base-600 text-gray-500 dark:text-base-400 hover:bg-gray-100 dark:hover:bg-base-700 flex items-center justify-center text-xs"
                  data-action="click->${id}#decrementQty" data-diver-key="${diverKey}" data-date="${date}" data-value="${p.value}" data-item-type="product">&minus;</button>
          <input type="number" min="1" value="${p.qty}" class="no-spinner w-10 text-center text-sm border border-gray-300 dark:border-base-600 dark:bg-base-800 dark:text-white rounded py-0.5"
                 data-action="change->${id}#updateQty" data-diver-key="${diverKey}" data-date="${date}" data-value="${p.value}" data-item-type="product">
          <button type="button" class="w-7 h-7 rounded border border-gray-300 dark:border-base-600 text-gray-500 dark:text-base-400 hover:bg-gray-100 dark:hover:bg-base-700 flex items-center justify-center text-xs"
                  data-action="click->${id}#incrementQty" data-diver-key="${diverKey}" data-date="${date}" data-value="${p.value}" data-item-type="product">+</button>
        </div>
        <div class="flex items-center shrink-0">
          <span class="text-gray-400 dark:text-base-500 text-sm mr-0.5">&times; $</span>
          <span class="text-sm font-medium text-gray-700 dark:text-base-200 w-20 text-right">${centsToDecimal(p.price_cents)}</span>
        </div>
        <button type="button" class="w-6 h-6 rounded-full text-gray-400 dark:text-base-500 hover:text-red-500 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-sm shrink-0"
                data-action="click->${id}#removeProduct" data-diver-key="${diverKey}" data-date="${date}" data-index="${index}">&times;</button>
      </div>`
  }

  /**
   * Render an addon row. Override in subclass to add price editing
   * or copy-to-all button.
   */
  _renderAddonRow(diverKey, date, a, index) {
    const id = this.actionIdentifier
    return `
      <div class="flex items-center gap-2 flex-wrap sm:flex-nowrap">
        <select class="flex-1 min-w-0 text-sm border border-gray-300 dark:border-base-600 rounded-md py-1.5 px-2 bg-white dark:bg-base-800 dark:text-white focus:border-green-400 dark:focus:border-green-500 focus:ring-1 focus:ring-green-400 dark:focus:ring-green-500"
                data-action="change->${id}#changeAddon" data-diver-key="${diverKey}" data-date="${date}" data-index="${index}">
          ${this._addonOptionsHtml(a.id)}
        </select>
        <div class="flex items-center gap-1 shrink-0">
          <button type="button" class="w-7 h-7 rounded border border-gray-300 dark:border-base-600 text-gray-500 dark:text-base-400 hover:bg-gray-100 dark:hover:bg-base-700 flex items-center justify-center text-xs"
                  data-action="click->${id}#decrementQty" data-diver-key="${diverKey}" data-date="${date}" data-value="${a.id}" data-item-type="addon">&minus;</button>
          <input type="number" min="1" value="${a.qty}" class="no-spinner w-10 text-center text-sm border border-gray-300 dark:border-base-600 dark:bg-base-800 dark:text-white rounded py-0.5"
                 data-action="change->${id}#updateQty" data-diver-key="${diverKey}" data-date="${date}" data-value="${a.id}" data-item-type="addon">
          <button type="button" class="w-7 h-7 rounded border border-gray-300 dark:border-base-600 text-gray-500 dark:text-base-400 hover:bg-gray-100 dark:hover:bg-base-700 flex items-center justify-center text-xs"
                  data-action="click->${id}#incrementQty" data-diver-key="${diverKey}" data-date="${date}" data-value="${a.id}" data-item-type="addon">+</button>
        </div>
        <div class="flex items-center shrink-0">
          <span class="text-gray-400 dark:text-base-500 text-sm mr-0.5">&times; $</span>
          <span class="text-sm font-medium text-gray-700 dark:text-base-200 w-20 text-right">${centsToDecimal(a.price_cents)}</span>
        </div>
        <button type="button" class="w-6 h-6 rounded-full text-gray-400 dark:text-base-500 hover:text-red-500 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-sm shrink-0"
                data-action="click->${id}#removeAddon" data-diver-key="${diverKey}" data-date="${date}" data-index="${index}">&times;</button>
      </div>`
  }

  _renderSharedFeesSection(diver) {
    const sharedData = diver.dates["__shared__"]
    if (!sharedData) return ""

    const productRows = sharedData.products.map((p, index) => this._renderProductRow("shared", "__shared__", p, index)).join("")
    const addonRows = sharedData.addons.map((a, index) => this._renderAddonRow("shared", "__shared__", a, index)).join("")
    const subtotal = this._diverSubtotal(diver)
    const hasItems = sharedData.products.length > 0 || sharedData.addons.length > 0
    const id = this.actionIdentifier

    return `
      <div class="border border-gray-200 dark:border-base-700 rounded-xl mb-3 bg-amber-50/30 dark:bg-amber-900/20" data-diver-section="shared">
        <div class="flex items-center justify-between flex-wrap gap-2 p-4 border-b border-gray-100 dark:border-base-700">
          <div class="flex items-center gap-2 flex-wrap min-w-0">
            <div class="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
              <svg class="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </div>
            <span class="text-sm font-semibold text-gray-900 dark:text-white">Shared Fees</span>
            <span class="text-xs text-gray-400 dark:text-base-500">Applied to all divers</span>
            ${subtotal > 0 ? `<span class="text-sm font-bold text-amber-600 dark:text-amber-400">${this._formatPrice(subtotal)}</span>` : ""}
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <button type="button" class="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 flex items-center gap-1"
                    data-action="click->${id}#addSharedProduct">
              <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M12 4v16m8-8H4"/></svg>
              Package
            </button>
            <button type="button" class="text-xs font-medium text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 flex items-center gap-1"
                    data-action="click->${id}#addSharedFee">
              <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M12 4v16m8-8H4"/></svg>
              Add-on
            </button>
          </div>
        </div>

        <div class="p-4">
          ${productRows ? `<div class="space-y-2 mb-2">${productRows}</div>` : ""}
          ${addonRows ? `<div class="space-y-2">${addonRows}</div>` : ""}
          ${!hasItems ? `<p class="text-xs text-gray-400 dark:text-base-500 italic text-center">No shared fees — add items that apply to the whole group (e.g., boat fee, service charge)</p>` : ""}
        </div>
      </div>`
  }

  renderOrderSummary() {
    if (!this.hasOrderSummaryTarget) return

    let hasItems = false
    let html = ""

    this.divers.forEach(diver => {
      const isShared = diver.position === 0
      const dates = Object.keys(diver.dates).sort()

      // Check if this diver is in package mode
      const isPackage = this._isPackageDiver && this._isPackageDiver(diver.id)
      const pkgProduct = isPackage ? this._getPackageProduct(diver.id) : null
      const pkgInfo = isPackage ? this._getPackageInfo(diver.id) : null

      if (isPackage && pkgProduct && pkgInfo) {
        // Package mode order summary
        hasItems = true
        const subtotal = this._diverSubtotal(diver)
        const labelColor = "text-blue-600 dark:text-blue-400"
        const borderColor = "border-blue-200 dark:border-blue-800"
        const packageItems = pkgProduct.package_items || []
        const assignedDates = pkgInfo.assignedDates || []
        const durationDays = pkgInfo.durationDays || 0

        // Build day breakdown lines
        const dayLines = []
        for (let dayNum = 1; dayNum <= durationDays; dayNum++) {
          const date = assignedDates[dayNum - 1]
          const dateLabel = date ? this._formatDateShort(date) : "not yet assigned"
          const dayItems = packageItems.filter(item => item.day_number === dayNum)
          const itemStr = dayItems.map(item => `${item.quantity}x ${this._escapeHtml(item.name)}`).join(", ")
          if (itemStr) {
            dayLines.push(`<div class="text-xs text-gray-400 dark:text-base-300 pl-5">Day ${dayNum} (${dateLabel}): ${itemStr}</div>`)
          }
        }

        // Package-wide items
        const allDayItems = packageItems.filter(item => item.day_number === null || item.day_number === undefined)
        if (allDayItems.length > 0) {
          const itemStr = allDayItems.map(item => `${item.quantity}x ${this._escapeHtml(item.name)}`).join(", ")
          dayLines.push(`<div class="text-xs text-gray-400 dark:text-base-300 pl-5">All Days: ${itemStr}</div>`)
        }

        // Warning in summary
        const selectedCount = dates.filter(d => d !== "__shared__" && !diver.dates[d]?.disabled).length
        const warningLine = selectedCount < durationDays
          ? `<div class="text-xs text-amber-600 dark:text-amber-400 pl-5 mt-1">⚠ ${durationDays} days required, ${selectedCount} selected</div>`
          : ""

        // Collect add-on items (non-package products added on top)
        const addonItems = []
        dates.forEach(date => {
          const dateData = diver.dates[date]
          if (date !== "__shared__" && dateData.disabled) return
          const dateLabel = date === "__shared__" ? "" : this._formatDateShort(date)

          dateData.addons.forEach(a => {
            const addon = this._addonById(a.id)
            if (addon) addonItems.push({ label: addon.name, qty: a.qty, price_cents: a.price_cents, isAddon: true, dateLabel })
          })

          // Non-package products (manually added)
          dateData.products.forEach(p => {
            if (p.value === pkgInfo.packageValue) return // skip the package product itself
            const prod = this._productByValue(p.value)
            if (prod) addonItems.push({ label: prod.title, qty: p.qty, price_cents: p.price_cents, isAddon: false, dateLabel })
          })
        })

        const addonLines = addonItems.map(item => {
          const lineTotal = item.qty * item.price_cents
          const icon = item.isAddon ? this.constructor.ADDON_ICON : this.constructor.PACKAGE_ICON
          return `
            <div class="flex justify-between items-start text-sm gap-2">
              <div class="inline-flex items-center gap-1.5 text-gray-700 dark:text-white leading-snug min-w-0">
                ${icon}
                <span>${this._escapeHtml(item.label)}${item.qty > 1 ? ` <span class="text-gray-400 dark:text-base-400 text-xs">&times;${item.qty}</span>` : ""}</span>
                ${item.dateLabel ? `<span class="text-gray-300 dark:text-base-500 text-xs">${item.dateLabel}</span>` : ""}
              </div>
              <span class="font-semibold ${item.isAddon ? "text-green-600 dark:text-green-400" : "text-blue-600 dark:text-blue-400"} shrink-0">${this._formatPrice(lineTotal)}</span>
            </div>`
        }).join("")

        html += `
          <div class="mb-4">
            <div class="text-xs font-bold ${labelColor} uppercase tracking-wide mb-2">${this._escapeHtml(diver.label)}</div>
            <div class="space-y-1">
              <div class="flex justify-between items-start text-sm gap-2">
                <div class="inline-flex items-center gap-1.5 text-gray-700 dark:text-white leading-snug min-w-0">
                  ${this.constructor.PACKAGE_ICON}
                  <span class="font-medium">${this._escapeHtml(pkgProduct.title)}</span>
                </div>
                <span class="font-semibold text-blue-600 dark:text-blue-400 shrink-0">${this._formatPrice(pkgProduct.price_cents)}</span>
              </div>
              ${dayLines.join("")}
              ${warningLine}
            </div>
            ${addonLines ? `<div class="space-y-1.5 mt-2">${addonLines}</div>` : ""}
            <div class="text-right text-sm font-medium text-gray-500 dark:text-base-300 mt-2 pt-1 border-0 border-t border-dashed ${borderColor}">${this._formatPrice(subtotal)}</div>
          </div>`

        return
      }

      // Standard (non-package) order summary — existing logic
      const items = []
      dates.forEach(date => {
        const dateData = diver.dates[date]
        if (date !== "__shared__" && dateData.disabled) return
        const dateLabel = date === "__shared__" ? "" : this._formatDateShort(date)

        dateData.products.forEach(p => {
          const prod = this._productByValue(p.value)
          if (prod) items.push({ label: prod.title, qty: p.qty, price_cents: p.price_cents, isAddon: false, dateLabel })
        })
        dateData.addons.forEach(a => {
          const addon = this._addonById(a.id)
          if (addon) items.push({ label: addon.name, qty: a.qty, price_cents: a.price_cents, isAddon: true, dateLabel })
        })
      })

      if (items.length === 0) return
      hasItems = true

      const subtotal = this._diverSubtotal(diver)
      const labelColor = isShared ? "text-amber-600 dark:text-amber-400" : "text-blue-600 dark:text-blue-400"
      const borderColor = isShared ? "border-amber-200 dark:border-amber-800" : "border-blue-200 dark:border-blue-800"

      html += `
        <div class="mb-4">
          <div class="flex justify-between items-center mb-2">
            <div class="text-xs font-bold ${labelColor} uppercase tracking-wide">${this._escapeHtml(diver.label)}</div>
          </div>
          <div class="space-y-1.5">
            ${items.map(item => {
              const lineTotal = item.qty * item.price_cents
              const priceColor = item.isAddon ? "text-green-600 dark:text-green-400" : "text-blue-600 dark:text-blue-400"
              const icon = item.isAddon ? this.constructor.ADDON_ICON : this.constructor.PACKAGE_ICON
              return `
              <div class="flex justify-between items-start text-sm gap-2">
                <div class="inline-flex items-center gap-1.5 text-gray-700 dark:text-white leading-snug min-w-0">
                  ${icon}
                  <span>${this._escapeHtml(item.label)}${item.qty > 1 ? ` <span class="text-gray-400 dark:text-base-400 text-xs">&times;${item.qty}</span>` : ""}</span>
                  ${item.dateLabel ? `<span class="text-gray-300 dark:text-base-500 text-xs">${item.dateLabel}</span>` : ""}
                </div>
                <span class="font-semibold ${priceColor} shrink-0">${this._formatPrice(lineTotal)}</span>
              </div>`
            }).join("")}
          </div>
          <div class="text-right text-sm font-medium text-gray-500 dark:text-base-300 mt-2 pt-1 border-0 border-t border-dashed ${borderColor}">${this._formatPrice(subtotal)}</div>
        </div>`
    })

    if (!hasItems) {
      html = `<p class="text-sm text-gray-400 dark:text-base-500 text-center py-4">No items selected</p>`
    }

    this.orderSummaryTarget.innerHTML = html

    if (this.hasGrandTotalTarget) {
      this.grandTotalTarget.textContent = this._formatPrice(this._grandTotal())
    }
  }

  syncHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return

    // Collect all unique enabled dates across all divers (excluding __shared__ and disabled)
    const allDates = new Set()
    this.divers.forEach(d => {
      Object.keys(d.dates).forEach(date => {
        if (date !== "__shared__" && !d.dates[date].disabled) allDates.add(date)
      })
    })

    let html = ""
    let optionIndex = 0

    // Dive dates
    const sortedDates = [...allDates].sort()
    sortedDates.forEach(date => {
      html += `<input type="hidden" name="booking[dive_dates][]" value="${date}">`
    })

    // Participants = total divers (excluding shared)
    const diverCount = this.divers.filter(d => d.position > 0).length
    html += `<input type="hidden" name="booking[participants]" value="${diverCount}">`

    // Diver names — persist labels for all non-shared divers
    this.divers.forEach(d => {
      if (d.position > 0) {
        html += `<input type="hidden" name="booking[diver_names][${d.position}]" value="${this._escapeAttr(d.label)}">`
      }
    })

    // Preferred date = first date
    if (sortedDates.length > 0) {
      html += `<input type="hidden" name="booking[preferred_date]" value="${sortedDates[0]}">`
    }

    // Booking dive options — iterate all divers and their enabled dates
    this.divers.forEach(diver => {
      const dates = Object.keys(diver.dates).sort()

      dates.forEach(date => {
        const dateData = diver.dates[date]
        // Skip disabled dates (but always include __shared__)
        if (date !== "__shared__" && dateData.disabled) return
        const effectiveDate = date === "__shared__" ? (sortedDates[0] || "") : date

        dateData.products.forEach(p => {
          const prod = this._productByValue(p.value)
          if (!prod) return
          const prefix = `booking[booking_dive_options_attributes][${optionIndex}]`
          html += `<input type="hidden" name="${prefix}[dive_selection]" value="${p.value}">`
          html += `<input type="hidden" name="${prefix}[category]" value="${prod.category}">`
          html += `<input type="hidden" name="${prefix}[dive_type]" value="${prod.dive_type}">`
          html += `<input type="hidden" name="${prefix}[quantity]" value="${p.qty}">`
          html += `<input type="hidden" name="${prefix}[unit_price_cents]" value="${p.price_cents}">`
          html += `<input type="hidden" name="${prefix}[is_addon]" value="false">`
          html += `<input type="hidden" name="${prefix}[dive_date]" value="${effectiveDate}">`
          if (diver.diverId) {
            html += `<input type="hidden" name="${prefix}[diver_id]" value="${diver.diverId}">`
          }
          html += `<input type="hidden" name="${prefix}[diver_position]" value="${diver.position}">`
          html += `<input type="hidden" name="${prefix}[diver_name]" value="${this._escapeAttr(diver.label)}">`
          optionIndex++
        })

        dateData.addons.forEach(a => {
          const addon = this._addonById(a.id)
          if (!addon) return
          const category = addon.category || "additional_offering"
          const diveType = category === "equipment"
            ? String(a.id).replace(/^equip_/, "")
            : (addon.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
          const selPrefix = category === "equipment" ? "equipment" : "addon"
          const prefix = `booking[booking_dive_options_attributes][${optionIndex}]`
          html += `<input type="hidden" name="${prefix}[dive_selection]" value="${selPrefix}:${diveType}">`
          html += `<input type="hidden" name="${prefix}[category]" value="${category}">`
          html += `<input type="hidden" name="${prefix}[dive_type]" value="${diveType}">`
          html += `<input type="hidden" name="${prefix}[quantity]" value="${a.qty}">`
          html += `<input type="hidden" name="${prefix}[unit_price_cents]" value="${a.price_cents}">`
          html += `<input type="hidden" name="${prefix}[is_addon]" value="true">`
          html += `<input type="hidden" name="${prefix}[dive_date]" value="${effectiveDate}">`
          if (diver.diverId) {
            html += `<input type="hidden" name="${prefix}[diver_id]" value="${diver.diverId}">`
          }
          html += `<input type="hidden" name="${prefix}[diver_position]" value="${diver.position}">`
          html += `<input type="hidden" name="${prefix}[diver_name]" value="${this._escapeAttr(diver.label)}">`
          optionIndex++
        })
      })
    })

    this.hiddenInputsTarget.innerHTML = html
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str || ""
    return div.innerHTML
  }

  _escapeAttr(str) {
    return (str || "").replace(/"/g, "&quot;").replace(/'/g, "&#39;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
